// UC25 — the acts an administrator can perform on one aggregate, in one place.
// The wireframe offers the same set from the register's row menu and from the
// fișa's header menu (wireframes/react/src/pages/AdminAgregari.tsx:655 `actions`,
// reused at :1496 with "Deschide fișa" and "Editează definiția" filtered out),
// so this hook is that `actions` function plus the dialogs it opens: a second
// copy on the detail page would be a second set of confirmations to keep in step.
// per docs/caiet-uc-spec.md §UC-AGG-07 §UC-AGG-08 §UC-AGG-10 §UC-AGG-11
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudModal, MudTag } from '@egov-moldova/mud-react';
import {
  dateInputToIso,
  isoToDateInput,
  onModalDismiss,
  useToast,
  type RowAction,
  DraftDateInput,
} from '@simsm/ui-shared';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { getDependencies, type AggregateDependency, type AggregateView } from './aggregates-domain';
import { todayIso } from '../../features/history/history-period';

export type AggregateActionId =
  | 'open'
  | 'edit'
  | 'refresh_range'
  | 'rebuild'
  | 'toggle_active'
  | 'delete';

export interface AggregateActionsOptions {
  // Where "Deschide fișa" and "Editează definiția" go. The fișa's own header
  // omits both, so the callbacks are optional.
  onOpen?: (aggregate: AggregateView) => void;
  onEdit?: (aggregate: AggregateView) => void;
  // The fișa has to leave the screen when its aggregate is gone.
  onDeleted?: (aggregate: AggregateView) => void;
  omit?: AggregateActionId[];
}

export interface AggregateActionsApi {
  actions: (aggregate: AggregateView) => RowAction[];
  dialogs: JSX.Element;
  // The aggregate a refresh or rebuild is currently running against, so the
  // caller can render its own spinner where the wireframe puts one.
  busyName: string | undefined;
}

export function useAggregateActions(options: AggregateActionsOptions = {}): AggregateActionsApi {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const [refreshTarget, setRefreshTarget] = useState<AggregateView | null>(null);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [blocked, setBlocked] = useState<{
    aggregate: AggregateView;
    dependencies: AggregateDependency[];
  } | null>(null);

  const omit = new Set(options.omit ?? []);

  // These five acts used to be announced by a banner and a `setTimeout` this
  // hook owned — a second, local implementation of the shared host, with its own
  // placement and its own timings, including a refusal that erased itself after
  // eight seconds whether or not it had been read. The host is now the only
  // thing that announces anything, and a refusal stays until it is dismissed.
  function announce(kind: 'success' | 'error', title: string, name?: string): void {
    toast({ variant: kind, title, text: name });
  }

  // Both screens read the same three keys, and the fișa's own detail and history
  // move with every act, so all four are dropped together.
  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin-aggregates'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-aggregate'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-aggregate-history'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-aggregate-rows'] });
  }

  const refresh = useMutation({
    mutationFn: (input: { name: string; rangeStart: string; rangeEnd: string }) =>
      apiClient
        .post(`/admin/aggregates/${input.name}/refresh`, {
          rangeStart: input.rangeStart,
          rangeEnd: input.rangeEnd,
        })
        .then(() => input.name),
    onSuccess: (name) => {
      invalidate();
      announce('success', t('aggregates.toast.refreshed'), name);
    },
    onError: (err: unknown, input) =>
      announce('error', t(apiErrorKey(err, 'aggregates.error.refresh_failed')), input.name),
  });

  const rebuild = useMutation({
    mutationFn: (name: string) =>
      apiClient.post(`/admin/aggregates/${name}/rebuild`, {}).then(() => name),
    onSuccess: (name) => {
      invalidate();
      announce('success', t('aggregates.toast.rebuilt'), name);
    },
    onError: (err: unknown, name) =>
      announce('error', t(apiErrorKey(err, 'aggregates.error.rebuild_failed')), name),
  });

  const setActive = useMutation({
    mutationFn: (input: { name: string; active: boolean }) =>
      apiClient
        .patch(`/admin/aggregates/${input.name}/active`, { active: input.active })
        .then(() => input),
    onSuccess: (input) => {
      invalidate();
      announce(
        'success',
        input.active ? t('aggregates.toast.activated') : t('aggregates.toast.deactivated'),
        input.name,
      );
    },
    // The fallback names the direction the toggle was going: "could not be
    // activated" is the wrong sentence for a deactivation that failed.
    onError: (err: unknown, input) =>
      announce(
        'error',
        t(
          apiErrorKey(
            err,
            input.active
              ? 'aggregates.error.activate_failed'
              : 'aggregates.error.deactivate_failed',
          ),
        ),
        input.name,
      ),
  });

  const remove = useMutation({
    mutationFn: (aggregate: AggregateView) =>
      apiClient.delete(`/admin/aggregates/${aggregate.name}`).then(() => aggregate),
    onSuccess: (aggregate) => {
      invalidate();
      announce('success', t('aggregates.toast.deleted'), aggregate.name);
      options.onDeleted?.(aggregate);
    },
    onError: (err: unknown, aggregate) =>
      announce('error', t(apiErrorKey(err, 'aggregates.error.delete_failed')), aggregate.name),
  });

  const busyName =
    refresh.isPending || rebuild.isPending
      ? ((refresh.variables?.name ?? rebuild.variables) as string | undefined)
      : undefined;

  const rangeInvalid = Boolean(rangeStart && rangeEnd && rangeStart > rangeEnd);

  function openRefresh(aggregate: AggregateView): void {
    setRangeStart('');
    setRangeEnd('');
    setRefreshTarget(aggregate);
  }

  async function askRebuild(aggregate: AggregateView): Promise<void> {
    const ok = await confirm({
      title: t('aggregates.confirm.rebuild.title'),
      body: t('aggregates.confirm.rebuild.body', { name: aggregate.displayName }),
      confirmLabel: t('aggregates.confirm.rebuild.confirm'),
      destructive: true,
    });
    if (ok) rebuild.mutate(aggregate.name);
  }

  async function askDeactivate(aggregate: AggregateView): Promise<void> {
    const ok = await confirm({
      title: t('aggregates.confirm.deactivate.title'),
      body: t('aggregates.confirm.deactivate.body', { name: aggregate.displayName }),
      confirmLabel: t('aggregates.confirm.deactivate.confirm'),
      destructive: true,
    });
    if (ok) setActive.mutate({ name: aggregate.name, active: false });
  }

  // A view something else reads is not the administrator's to drop, so the
  // readers are looked up before the destructive dialog is even offered
  // (UC-AGG-06: aggregates, alert rules and dashboards all read these rows).
  // A caiet-configured aggregate never reaches here — the menu entry is disabled
  // for it, and the server refuses with AGGREGATE_PREDEFINED (UC-AGG-11).
  async function askDelete(aggregate: AggregateView): Promise<void> {
    let dependencies: AggregateDependency[] = [];
    try {
      dependencies = await getDependencies(aggregate.name);
    } catch (err) {
      announce(
        'error',
        t(apiErrorKey(err, 'aggregates.error.dependencies_failed')),
        aggregate.name,
      );
      return;
    }
    if (dependencies.length > 0) {
      setBlocked({ aggregate, dependencies });
      return;
    }
    const ok = await confirm({
      title: t('aggregates.confirm.delete.title'),
      body: t('aggregates.confirm.delete.body', { name: aggregate.displayName }),
      confirmLabel: t('aggregates.confirm.delete.confirm'),
      destructive: true,
    });
    if (ok) remove.mutate(aggregate);
  }

  function actions(aggregate: AggregateView): RowAction[] {
    const inactive = aggregate.status === 'archived';
    const busy = busyName === aggregate.name;
    const all: (RowAction & { id: AggregateActionId })[] = [
      {
        id: 'open',
        label: t('aggregates.action.open'),
        onSelect: () => options.onOpen?.(aggregate),
      },
      {
        id: 'edit',
        label: t('aggregates.action.edit_definition'),
        onSelect: () => options.onEdit?.(aggregate),
      },
      {
        id: 'refresh_range',
        label: t('aggregates.action.refresh_range'),
        onSelect: () => openRefresh(aggregate),
        disabled: inactive || busy,
        reason: inactive
          ? t('aggregates.reason.inactive')
          : t('aggregates.reason.operation_running'),
      },
      {
        id: 'rebuild',
        label: t('aggregates.action.rebuild'),
        onSelect: () => void askRebuild(aggregate),
        disabled: inactive || busy,
        reason: inactive
          ? t('aggregates.reason.inactive')
          : t('aggregates.reason.operation_running'),
        separator: true,
      },
      {
        id: 'toggle_active',
        label: inactive ? t('aggregates.action.activate') : t('aggregates.action.deactivate'),
        onSelect: () =>
          inactive
            ? setActive.mutate({ name: aggregate.name, active: true })
            : void askDeactivate(aggregate),
        danger: !inactive,
        disabled: setActive.isPending,
      },
      {
        id: 'delete',
        label: t('aggregates.action.delete'),
        onSelect: () => void askDelete(aggregate),
        danger: true,
        disabled: remove.isPending || aggregate.predefined,
        // Only the caiet-configured refusal has something to explain; a delete
        // that is briefly disabled because another one is in flight does not.
        ...(aggregate.predefined ? { reason: t('aggregates.reason.predefined') } : {}),
      },
    ];
    return all.filter((action) => !omit.has(action.id)).map(({ id: _id, ...action }) => action);
  }

  const dialogs = (
    <>
      <MudModal
        closeLabel={t('common.close')}
        open={refreshTarget !== null}
        size="md"
        titleText={t('aggregates.refresh.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; the date inputs inside do not emit their own mudClose today,
        // so the guard is a no-op here until one does.
        onMudClose={onModalDismiss(() => setRefreshTarget(null))}
      >
        {refreshTarget ? (
          <div className="simsm-aggregate__dialog">
            <p className="simsm-aggregate__dialog-name">{refreshTarget.displayName}</p>
            <div className="simsm-aggregate__dialog-row">
              <DraftDateInput
                placeholder={t('common.date.placeholder')}
                size="md"
                max={todayIso()}
                label={t('aggregates.refresh.from')}
                helperText={t('aggregates.refresh.from_hint')}
                value={isoToDateInput(rangeStart)}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setRangeStart(dateInputToIso(e.detail.value))
                }
              />
              <DraftDateInput
                placeholder={t('common.date.placeholder')}
                size="md"
                max={todayIso()}
                label={t('aggregates.refresh.to')}
                invalid={rangeInvalid}
                helperText={
                  rangeInvalid
                    ? t('aggregates.refresh.range_error')
                    : t('aggregates.refresh.to_hint')
                }
                value={isoToDateInput(rangeEnd)}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setRangeEnd(dateInputToIso(e.detail.value))
                }
              />
            </div>
          </div>
        ) : null}
        <div slot="actions" className="simsm-aggregate__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setRefreshTarget(null)}
          >
            {t('common.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={rangeInvalid}
            onClick={() => {
              if (!refreshTarget) return;
              refresh.mutate({ name: refreshTarget.name, rangeStart, rangeEnd });
              setRefreshTarget(null);
            }}
          >
            {t('aggregates.refresh.confirm')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={blocked !== null}
        size="md"
        titleText={t('aggregates.blocked.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setBlocked(null))}
      >
        {blocked ? (
          <div className="simsm-aggregate__dialog">
            <p>{t('aggregates.blocked.body', { name: blocked.aggregate.displayName })}</p>
            <ul className="simsm-aggregate__dependency-list">
              {blocked.dependencies.map((dependency) => (
                <li key={`${dependency.kind}-${dependency.ref}`}>
                  <MudTag
                    variant="status"
                    type="subtle"
                    semantic="neutral"
                    label={t(`aggregates.dependency.kind.${dependency.kind}`)}
                  />
                  <span>{dependency.label}</span>
                </li>
              ))}
            </ul>
            <p className="simsm-aggregate__dialog-hint">{t('aggregates.blocked.hint')}</p>
          </div>
        ) : null}
        <div slot="actions" className="simsm-aggregate__modal-actions">
          <MudButton shape="circular" variant="primary" size="md" onClick={() => setBlocked(null)}>
            {t('common.close')}
          </MudButton>
        </div>
      </MudModal>
    </>
  );

  return { actions, dialogs, busyName };
}
